import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

import WpButton from 'Wpds/components/button/WpButton';
import WpIcon from 'Wpds/components/icon/WpIcon';

import VideoRecorderModal from './VideoRecorderModal/VideoRecorderModal';

import './VideoRecorder.scss';

// Hooks
import { useApp } from 'Shared/utils/hooks/use-app';

const STOPPED = 'stopped';
const RECORDING = 'recording';
const PAUSED = 'paused';
const REVIEWING = 'reviewing';
const COUNTDOWN = 'countdown';

const MIME_TYPES = [
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8,opus',
  'video/webm;codecs=vp8',
  'video/webm',
  'video/mp4',
];

let recordedBlobs;
let mediaRecorder;
let mimeType;

const VideoRecorder = ({ onHide, onSave, defaultCapture = 'webcam' }) => {
  const { app } = useApp();
  const videoPlayer = useRef(null);

  const [stream, setStream] = useState(null);
  const streamRef = useRef(stream);
  streamRef.current = stream;

  const [captureWebcam, setCaptureWebcam] = useState(defaultCapture === 'webcam');
  const [captureMicrophone, setCaptureMicrophone] = useState(defaultCapture === 'audio');
  const [captureScreen, setCaptureScreen] = useState(defaultCapture === 'screen');
  const [recordingStatus, setRecordingStatus] = useState(STOPPED);
  const [localVideoURL, setLocalVideoURL] = useState(null);
  const [countdownTimeout, setCountdownTimeout] = useState(null);
  const [countdownTimer, setcountdownTimer] = useState(null);
  const [error, setError] = useState(null);
  const countdownRef = useRef(countdownTimer);
  countdownRef.current = countdownTimer;

  const closeVideoRecorder = useCallback(() => {
    if (countdownTimeout) {
      clearTimeout(countdownTimeout);
      setCountdownTimeout(null);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    onHide();
  }, [countdownTimeout, onHide]);

  const fetchStream = useCallback(async () => {
    let capturedStream;
    try {
      if (captureWebcam) {
        capturedStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: { width: 1280, height: 720 },
        });
        setError(null);
      }

      if (captureScreen) {
        const videoStream = await navigator.mediaDevices.getDisplayMedia({
          video: { width: 1280, height: 720 },
        });
        const voiceStream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
        const tracks = [...videoStream.getTracks(), ...voiceStream.getAudioTracks()];
        capturedStream = new MediaStream(tracks);
        setError(null);
      }

      if (captureMicrophone) {
        capturedStream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: { exact: true } },
          video: false,
        });
        setError(null);
      }

      setStream(capturedStream);

      if (videoPlayer.current) {
        videoPlayer.current.srcObject = capturedStream;
      }
    } catch (e) {
      if (e.name === 'NotAllowedError') {
        setError('Please enable access to your camera.');
      } else {
        setError('Unable to connect to the camera.');
      }

      setCaptureScreen(false);
      setCaptureMicrophone(false);
      setCaptureWebcam(true);
      setStream(null);
    }
  }, [captureMicrophone, captureScreen, captureWebcam]);

  useEffect(() => {
    window.analytics.track('Clicked Video', { app_id: app.id });

    fetchStream();
    return function cleanup() {
      if (!streamRef.current?.getTracks) {
        return;
      }
      streamRef.current.getTracks().forEach(track => track.stop());
    };
  }, [captureWebcam, captureMicrophone, captureScreen, app.id, fetchStream]);

  const startReview = useCallback(() => {
    setRecordingStatus(REVIEWING);
    mediaRecorder.stop();

    // Creating a blob from 'video/webm;codecs=vp8,opus' mimeType causes issues
    // but it works if you strip out the ';codecs=vp8,opus' part
    // No issues with 'video/webm;codecs=vp8'
    const type = mimeType.includes(',') ? mimeType.split(';')[0] : mimeType;
    const recordedBuffer = new Blob(recordedBlobs, { type });
    setLocalVideoURL(window.URL.createObjectURL(recordedBuffer));
  }, [captureMicrophone]);

  const startRecording = useCallback(() => {
    // If recording is currently paused, then don't create a new stream
    if (recordingStatus === PAUSED) {
      mediaRecorder.resume();
    } else {
      recordedBlobs = [];

      const videoMimeType = MIME_TYPES.find(type => MediaRecorder.isTypeSupported(type));
      try {
        mimeType = captureMicrophone ? 'audio/webm' : videoMimeType;
        mediaRecorder = new MediaRecorder(stream, {
          mimeType,
          bitsPerSecond: 800000,
        });
      } catch (e) {
        setRecordingStatus(STOPPED);
        setError('There was an issue starting the recording. Please reload the page and try again');
        console.error('Exception while creating MediaRecorder:', e);
        throw e;
      }

      mediaRecorder.onstop = event => {
        console.log('Recorder stopped: ', event);
      };

      mediaRecorder.ondataavailable = event => {
        if (event.data && event.data.size > 0) {
          recordedBlobs.push(event.data);
        }
      };

      mediaRecorder.onerror = event => {
        setError(event.error);
      };

      mediaRecorder.start(10); // collect 10ms of data
    }

    window.analytics.track('Started Recording', { app_id: app.id });
    setRecordingStatus(RECORDING);
    console.log('MediaRecorder started', mediaRecorder);
  }, [app.id, captureMicrophone, captureScreen, captureWebcam, recordingStatus, startReview, stream]);

  const stopRecording = useCallback(() => {
    if (recordingStatus === STOPPED) {
      return;
    }
    window.analytics.track('Stopped Recording', { app_id: app.id });
    startReview();
  }, [app.id, recordingStatus, startReview]);

  const toggleWebcam = () => {
    if (recordingStatus === RECORDING) {
      return;
    }

    setCaptureScreen(false);
    setCaptureMicrophone(false);
    setCaptureWebcam(true);
    window.analytics.track('Switched to Webcam', { app_id: app.id });
  };

  const toggleScreen = () => {
    if (recordingStatus === RECORDING) {
      return;
    }

    setCaptureWebcam(false);
    setCaptureMicrophone(false);
    setCaptureScreen(true);
    window.analytics.track('Switched to Screenshare', { app_id: app.id });
  };

  const toggleMicrophone = () => {
    if (recordingStatus === RECORDING) {
      return;
    }

    setCaptureWebcam(false);
    setCaptureScreen(false);
    setCaptureMicrophone(true);
    window.analytics.track('Switched to Audio', { app_id: app.id });
  };

  const updateCountdown = () => {
    if (!countdownRef.current) {
      setcountdownTimer(3);
      setCountdownTimeout(setTimeout(() => updateCountdown(), 1000));
    } else if (countdownRef.current <= 1) {
      setcountdownTimer(null);
      startRecording();
    } else {
      setCountdownTimeout(setcountdownTimer(countdownRef.current - 1));
      setTimeout(() => updateCountdown(), 1000);
    }
  };

  const startCountdown = () => {
    if (!stream) {
      return;
    }
    if (recordingStatus === COUNTDOWN) {
      return;
    }

    if (countdownTimeout) {
      clearTimeout(countdownTimeout);
    }
    setRecordingStatus(COUNTDOWN);
    updateCountdown();
  };

  if (recordingStatus === REVIEWING) {
    return (
      <VideoRecorderModal
        closeVideoRecorder={closeVideoRecorder}
        setRecordingStatus={setRecordingStatus}
        onSave={onSave}
        recordedBlobs={recordedBlobs}
        frameGrab={false}
        fetchStream={fetchStream}
        captureMicrophone={captureMicrophone}
        localVideoURL={localVideoURL}
      />
    );
  }

  return (
    <>
      {recordingStatus === COUNTDOWN && (
        <div className="countdown-container d-flex flex-column justify-content-center align-items-center">
          <div className="countdown-text">Recording in...</div>
          <div className="countdown-number text-primary">{countdownTimer}</div>
        </div>
      )}
      <div className="video-recorder-container">
        <div className="close-button">
          <WpButton variant="tertiary" onClick={closeVideoRecorder}>
            <WpIcon name="times" />
          </WpButton>
        </div>

        {error && <div className="video-error-message alert alert-danger small">{error.toString()}</div>}

        {captureWebcam && <video ref={videoPlayer} autoPlay playsInline muted loop />}
        {!captureWebcam && <div style={{ height: '30px' }} />}

        <div className="buttons mt-3 mb-3 d-flex justify-content-between">
          <button
            type="button"
            onClick={toggleWebcam}
            className={`source-button option ${captureWebcam ? 'on' : 'off'}`}
          >
            <span className="source-button-icon">
              <WpIcon name="video" />
            </span>
            <span>Camera</span>
          </button>
          <button
            type="button"
            onClick={toggleScreen}
            className={`source-button option ${captureScreen ? 'on' : ' off'}`}
          >
            <span className="source-button-icon">
              <WpIcon name="desktop" />
            </span>
            <span>Screen</span>
          </button>
          <button
            type="button"
            onClick={toggleMicrophone}
            className={`source-button option ${captureMicrophone ? 'on' : 'off'}`}
          >
            <span className="source-button-icon">
              <WpIcon name="microphone" />
            </span>
            <span>Voice Only</span>
          </button>
        </div>
        {recordingStatus !== RECORDING && (
          <WpButton size="lg" block disabled={!stream} onClick={startCountdown}>
            <WpIcon prefix="fas" name="circle" />
            Start Recording
          </WpButton>
        )}
        {recordingStatus === RECORDING && (
          <WpButton size="lg" block variant="secondary" onClick={stopRecording}>
            <WpIcon prefix="fas" name="stop" />
            Stop Recording
          </WpButton>
        )}
      </div>
    </>
  );
};

VideoRecorder.propTypes = {
  onHide: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  defaultCapture: PropTypes.string,
};

export default VideoRecorder;
